/** Formatação no padrão brasileiro (R$, vírgula decimal); datas e horas no formato da Administração › Geral. */
import { formatClock, formatIsoDate } from '@pmis/shared';
import { branding } from './branding';

/** Data (AAAA-MM-DD) no formato da plataforma: dd/mm/aaaa (padrão) ou mm/dd/aaaa. */
export const fmtDate = (d: string | null | undefined) => formatIsoDate(d, branding().formats.date);

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const brl2 = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const usd = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const n0 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
const n1 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });
const compact = new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 });

export const money = (v: number | null | undefined, cents = false) => (v == null ? '—' : (cents ? brl2 : brl).format(v));
export const dollars = (v: number | null | undefined) => (v == null ? '—' : usd.format(v));
export const moneyCompact = (v: number | null | undefined) => (v == null ? '—' : `R$ ${compact.format(v)}`);
export const int = (v: number | null | undefined) => (v == null ? '—' : n0.format(v));
export const dec1 = (v: number | null | undefined) => (v == null ? '—' : n1.format(v));
export const pct = (v: number | null | undefined) => (v == null ? '—' : `${n0.format(v)}%`);
export const hours = (v: number | null | undefined) => (v == null ? '—' : `${n1.format(v)} h`);
export const index = (v: number | null | undefined) => (v == null ? '—' : v.toFixed(2).replace('.', ','));

/** Data e hora do banco (UTC, sem fuso: "2026-10-10 17:23:40.123") ou ISO com fuso ("…Z", "…-03:00", com nanossegundos). */
const parseUtc = (s: string) => {
  const t = s.trim().replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1');
  return new Date(/(Z|[+-]\d{2}:?\d{2})$/i.test(t) ? t : `${t}Z`);
};

/** dd/mm/aaaa hh:mm (horário de Brasília) a partir do UTC do banco (ou de uma data ISO com fuso). */
export function fmtDateTime(utc: string | null | undefined): string {
  if (!utc) return '—';
  const d = parseUtc(utc);
  if (Number.isNaN(d.getTime())) return '—';
  // Data e hora no horário de Brasília, nos formatos da plataforma.
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    .formatToParts(d).map((x) => [x.type, x.value]));
  return `${fmtDate(`${p.year}-${p.month}-${p.day}`)} ${formatClock(`${p.hour}:${p.minute}`, branding().formats.time)}`;
}

/** "há 3 dias", "hoje". */
export function ago(utcOrDate: string | null | undefined): string {
  if (!utcOrDate) return 'nunca';
  const d = utcOrDate.length <= 10 ? new Date(`${utcOrDate}T12:00:00Z`) : parseUtc(utcOrDate);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return 'hoje';
  if (days === 1) return 'ontem';
  if (days < 30) return `há ${days} dias`;
  if (days < 365) return `há ${Math.round(days / 30)} meses`;
  return `há ${(days / 365).toFixed(1).replace('.', ',')} anos`;
}

export const minutesToHours = (m: number) => Math.round((m / 60) * 100) / 100;
/** "1:30" ou "1,5" → minutos. */
export function parseHours(s: string): number | null {
  const t = s.trim().replace(',', '.');
  if (!t) return null;
  const hm = /^(\d{1,2}):(\d{2})$/.exec(t);
  if (hm) return Number(hm[1]) * 60 + Number(hm[2]);
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 60) : null;
}
export const fmtMinutes = (m: number) => `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
